import React, { useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import { Icon, type IconType } from 'admin/components/common/Icon';
import { useTheme } from 'admin/components/common/Theme';
import TokenPill from 'admin/components/pages/Story/CardEditor/Inspector/PropField/TokenPill';
import FillPreview from 'admin/components/pages/Story/CardEditor/Inspector/PropField/FillPreview';
import { PROP_FIELD_ICONS } from 'admin/components/pages/Story/CardEditor/Inspector/config/prop-fields-config';
import {
	getDesignPresetInheritedAssignment,
	getDesignPresetTokenId,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
} from 'common/design-tokens';
import { getBorderLineStyle, getBorderSideWidths, hasBorder } from 'common/design-tokens/borders';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import t from 'utils/translate';
import { getDesignPresetDisplayValue } from './design-preset-display';
import DesignPresetEditorHeader from './DesignPresetEditorHeader';
import { formatPresetNumber, getPresetCellSource, type PresetCellSource } from './design-preset-cell';
import type { PresetAssignmentChange, PresetPropertyGroup } from './design-preset-editing';
import { BorderGroup, RadiusGroup, SpaceGroup } from './DesignPresetGroupEditors';
import css from './DesignPresetsEditor.scss';

type Entry = { source: PresetCellSource; value?: string | number; token: boolean };

// The Inspector's Space and Radius icons, in the Inspector's grid order
const SIDE_ICONS: Record<string, IconType> = {
	Left: PROP_FIELD_ICONS.SPACE_LEFT,
	Right: PROP_FIELD_ICONS.SPACE_RIGHT,
	Top: PROP_FIELD_ICONS.SPACE_TOP,
	Bottom: PROP_FIELD_ICONS.SPACE_BOTTOM,
};
const CORNERS: { property: DesignPresetProperty; icon: IconType }[] = [
	{ property: 'borderTopLeftRadius', icon: PROP_FIELD_ICONS.RADIUS_TL },
	{ property: 'borderTopRightRadius', icon: PROP_FIELD_ICONS.RADIUS_TR },
	{ property: 'borderBottomLeftRadius', icon: PROP_FIELD_ICONS.RADIUS_BL },
	{ property: 'borderBottomRightRadius', icon: PROP_FIELD_ICONS.RADIUS_BR },
];

function EntryValue({ entry }: { entry: Entry }) {
	// One decimal: four values share a cell
	const text = formatPresetNumber(entry.value, 1) || '—';
	if (entry.token)
		return <TokenPill label={text} value={entry.value === undefined ? t('designTokens.unresolved') : text} />;
	return <span>{text}</span>;
}

/** One side or corner: its icon, then its value right beside it, as a Space grid field */
function IconEntry({ icon, entry }: { icon: IconType; entry: Entry }) {
	return (
		<span className={css.groupValue} data-source={entry.source}>
			<Icon type={icon} width={14} height={14} color="currentColor" className={css.groupIcon} />
			<EntryValue entry={entry} />
		</span>
	);
}

export default function DesignPresetGroupCell({
	group,
	preset,
	platform,
	platformLabel,
	design,
	mediaQuery,
	modeSelection,
	fonts,
	disabled,
	onChange,
}: {
	group: PresetPropertyGroup;
	preset: DesignPreset;
	platform: 'base' | StoryDefaultPlatform;
	platformLabel: string;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	modeSelection: Record<string, string>;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onChange: (changes: readonly PresetAssignmentChange[]) => void;
}) {
	const [open, setOpen] = useState(false);
	const { theme } = useTheme();
	const getEntry = (property: DesignPresetProperty): Entry => {
		const assignment = preset.properties[property];
		const assigned = platform === 'base' ? assignment?.base : assignment?.platforms?.[platform];
		const inherited =
			platform === 'base' ? undefined : getDesignPresetInheritedAssignment(assignment, platform, mediaQuery);
		const display = assignment
			? getDesignPresetDisplayValue({ property, assignment, platform, mediaQuery, design, modeSelection })
			: undefined;
		return {
			source: getPresetCellSource({ assigned, inherited, isBase: platform === 'base' }),
			value: display?.value,
			token: getDesignPresetTokenId(assigned ?? inherited?.value) !== undefined,
		};
	};
	const spacing = group.id === 'innerSpace' || group.id === 'outerSpace';
	const hasAssignments = group.properties.some(property => {
		const value = preset.properties[property];
		return platform === 'base' ? value?.base !== undefined : value?.platforms?.[platform] !== undefined;
	});
	const context = { preset, platform, design, mediaQuery, modeSelection, fonts, disabled, onChange };

	let summary: React.ReactNode;
	if (spacing) {
		// Space grid order: left, right, then top, bottom
		const sides = ['Left', 'Right', 'Top', 'Bottom'].map(side => ({
			side,
			property: group.properties.find(property => property.endsWith(side)) as DesignPresetProperty,
		}));
		summary = (
			<span className={css.groupGrid}>
				{sides.map(({ side, property }) => (
					<IconEntry key={side} icon={SIDE_ICONS[side]} entry={getEntry(property)} />
				))}
			</span>
		);
	} else if (group.id === 'radius') {
		const all = getEntry('borderRadius');
		summary = (
			<span className={css.groupGrid}>
				{CORNERS.map(({ property, icon }) => {
					const corner = getEntry(property);
					return <IconEntry key={property} icon={icon} entry={corner.source === 'empty' ? all : corner} />;
				})}
			</span>
		);
	} else {
		const color = getEntry('borderColor');
		const width = getEntry('borderWidth');
		const style = getEntry('borderStyle');
		const on = hasBorder(style.value);
		const widths = getBorderSideWidths(width.value ?? '0', style.value);
		const drawn = widths.map(side => on && Number.parseFloat(side) !== 0);
		const drawnWidths = widths.filter((_, index) => drawn[index]);
		const oneWidth = drawnWidths.length > 0 && drawnWidths.every(side => side === drawnWidths[0]);
		summary =
			style.value === undefined ? (
				<span className={css.groupValue} data-source="empty">
					—
				</span>
			) : !on ? (
				<span className={css.groupValue} data-source={style.source}>
					None
				</span>
			) : (
				<span className={css.groupRow}>
					<span className={css.groupValue} data-source={color.source}>
						{/* The same round swatch as a color field; an unset color shows its empty state */}
						<FillPreview
							value={color.value === undefined ? '' : String(color.value)}
							className={css.swatch}
						/>
					</span>
					{/* One width when the drawn sides agree; the sides glyph shows which are drawn either way */}
					{oneWidth && !width.token ? (
						<span className={css.groupValue} data-source={width.source}>
							<EntryValue entry={{ ...width, value: drawnWidths[0] }} />
						</span>
					) : null}
					<span className={css.groupValue} data-source={style.source}>
						<i className={css.lineGlyph} data-style={getBorderLineStyle(style.value)} />
						<i
							className={css.sidesGlyph}
							aria-hidden
							style={{
								borderTopStyle: drawn[0] ? 'solid' : 'dotted',
								borderRightStyle: drawn[1] ? 'solid' : 'dotted',
								borderBottomStyle: drawn[2] ? 'solid' : 'dotted',
								borderLeftStyle: drawn[3] ? 'solid' : 'dotted',
								borderTopColor: drawn[0] ? 'currentcolor' : undefined,
								borderRightColor: drawn[1] ? 'currentcolor' : undefined,
								borderBottomColor: drawn[2] ? 'currentcolor' : undefined,
								borderLeftColor: drawn[3] ? 'currentcolor' : undefined,
							}}
						/>
					</span>
				</span>
			);
	}

	// Reset clears the whole group on this platform, as a single property's cell does
	const reset = () => {
		setOpen(false);
		onChange(group.properties.map(property => ({ property, value: undefined })));
	};
	const resetLabel = t(platform === 'base' ? 'presets.clearAssignment' : 'presets.revert');

	return (
		<div className={css.assignment} data-open={open}>
			<Popover
				trigger="click"
				placement="bottomLeft"
				open={open && !disabled}
				onOpenChange={setOpen}
				styles={{ container: { padding: 0 } }}
				content={
					open ? (
						<div
							className={css.groupEditor}
							data-theme={theme}
							onKeyDown={event => {
								if (event.key === 'Escape') {
									event.stopPropagation();
									setOpen(false);
								}
							}}
						>
							<DesignPresetEditorHeader label={group.label} platform={platform} mediaQuery={mediaQuery} />
							{/* Keyed by preset and platform, so a group's bound state starts from what it edits */}
							{spacing ? (
								<SpaceGroup key={`${preset.id}:${platform}`} group={group} {...context} />
							) : group.id === 'radius' ? (
								<RadiusGroup key={`${preset.id}:${platform}`} {...context} />
							) : (
								<BorderGroup key={`${preset.id}:${platform}`} {...context} />
							)}
						</div>
					) : null
				}
			>
				<button
					type="button"
					className={css.groupTrigger}
					disabled={disabled}
					data-assigned={hasAssignments}
					aria-label={`Edit ${group.label} · ${platformLabel}`}
					aria-expanded={open}
					onClick={() => setOpen(!open)}
				>
					{summary}
				</button>
			</Popover>
			<span className={css.cellActions}>
				{hasAssignments ? (
					<Tooltip title={resetLabel}>
						<button
							type="button"
							className={css.revert}
							disabled={disabled}
							aria-label={`${resetLabel}: ${group.label}`}
							onClick={reset}
						>
							<UndoOutlined />
						</button>
					</Tooltip>
				) : null}
			</span>
		</div>
	);
}
